import {
  type AcquisitionBenefit,
  type PartnerAcquisitionLink,
  type AcquisitionNumbers,
  type AcquisitionSeriesPoint,
  type PartnerAcquisitionCommissions,
  type PartnerAcquisitionParams,
} from '@/api/routes/partners/partners.acquisition.api';
import {
  type AcquisitionCommissionSummary,
  type AcquisitionIndicators,
  type AcquisitionPeriodDays,
} from '@/domain/acquisition/types';
import { formatCampaignPeriod } from '@/domain/results/utils';
import { getActiveLocale } from '@/i18n/activeLocale';
import { translateFile, translatedMap } from '@/i18n';

export const ACQUISITION_PERIODS: AcquisitionPeriodDays[] = [30, 90];

export const DEFAULT_ACQUISITION_PERIOD: AcquisitionPeriodDays = 30;

/** The API counts São Paulo days. */
const ACQUISITION_TIMEZONE = 'America/Sao_Paulo';

export const acquisitionLinkStatusLabel = translatedMap('domain.acquisition.utils', {
  active: 'status.active',
  paused: 'status.paused',
} as const);

/** The window in the URL (`?days=`), falling back to the default for anything unknown. */
export const parseAcquisitionPeriod = (value: string | null): AcquisitionPeriodDays =>
  ACQUISITION_PERIODS.find((days) => String(days) === value) ?? DEFAULT_ACQUISITION_PERIOD;

/** `YYYY-MM-DD` of the date in São Paulo. */
export const toSaoPauloDay = (date: Date): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: ACQUISITION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

const shiftDay = (day: string, delta: number): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date + delta)).toISOString().slice(0, 10);
};

/** The last N São Paulo days, today included, as `GET /partners/acquisition` reads them. */
export const toAcquisitionParams = (
  days: AcquisitionPeriodDays,
  now = new Date(),
): Required<PartnerAcquisitionParams> => {
  const to = toSaoPauloDay(now);
  return { from: shiftDay(to, -(days - 1)), to };
};

/** Clicks, signups and paying of a total or a link; a link without numbers counts zero. */
export const getAcquisitionIndicators = (
  numbers: AcquisitionNumbers | null,
): AcquisitionIndicators => ({
  clicks: numbers?.clicks ?? 0,
  uniqueClicks: numbers?.uniqueClicks ?? 0,
  signups: numbers?.stages.signed_up ?? 0,
  paying: numbers?.stages.paying ?? 0,
});

export const getCommissionSummary = (
  commissions: PartnerAcquisitionCommissions,
): AcquisitionCommissionSummary => ({
  toReceiveCents: commissions.accruedCents + commissions.approvedCents,
  paidCents: commissions.paidCents,
});

/** True when the period had any click, signup or paying person. */
export const hasSeriesActivity = (series: AcquisitionSeriesPoint[]): boolean =>
  series.some((point) => point.clicks > 0 || point.signups > 0 || point.paying > 0);

/** "1.240"; at most one decimal (a person split between links never shows here, but stays safe). */
export const formatAcquisitionCount = (value: number): string =>
  new Intl.NumberFormat(getActiveLocale(), { maximumFractionDigits: 1 }).format(value);

/** "R$ 1.250,00". */
export const formatCommission = (cents: number): string =>
  new Intl.NumberFormat(getActiveLocale(), { style: 'currency', currency: 'BRL' }).format(
    cents / 100,
  );

/** "7 dias a mais de teste"; an unknown kind shows nothing. */
export const formatAcquisitionBenefit = (benefit: AcquisitionBenefit | null): string | null => {
  if (benefit?.kind !== 'trial_days') return null;
  return translateFile('domain.acquisition.utils', 'benefit.trialDays', { count: benefit.value });
};

/** "Outubro · Corrida · 1 out 2026 a 31 out 2026"; just the name without a start. */
export const formatLinkCampaign = (
  campaign: NonNullable<PartnerAcquisitionLink['campaign']>,
): string =>
  campaign.startsAt
    ? `${campaign.name} · ${formatCampaignPeriod({ startsAt: campaign.startsAt, endsAt: campaign.endsAt })}`
    : campaign.name;
